import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { searchProducts } from '../api/endpoints';
import type { ProductSearchResultItem } from '../types/api';
import { useOptionalUser } from '../contexts/UserContext';
import { useAffiliateConsent } from '../hooks/useAffiliateConsent';
import { AffiliateDisclosureGate } from './AffiliateDisclosureGate';

interface ProductSearchModalProps {
  keyword: string;
  onClose: () => void;
  titleOverride?: string;
}

const spring = { type: 'spring', stiffness: 420, damping: 36, mass: 0.9 } as const;
const EASE = [0.22, 0.61, 0.36, 1] as const;

export const ProductSearchModal: React.FC<ProductSearchModalProps> = ({ keyword, onClose, titleOverride }) => {
  const [results, setResults] = useState<ProductSearchResultItem[]>([]);
  const [loading, setLoading] = useState(true);
  const userCtx = useOptionalUser();
  const showDisclosure = !!userCtx?.showAffiliateDisclosure;
  const disclosureText = userCtx?.disclosureText ?? '';

  // When the platform requires the affiliate disclosure, gate the popup behind
  // it: show the consent screen first and DON'T call the product API until the
  // user agrees. Where disclosure isn't required, behave exactly as before.
  const consent = useAffiliateConsent();
  const gateNeeded = showDisclosure && !consent.enabled;

  useEffect(() => {
    if (gateNeeded) return; // never pre-fire the search while the gate is up
    setLoading(true);
    searchProducts({ product_keywords: keyword, limit: 10 })
      .then(setResults)
      .catch(() => { setResults([]); })
      .finally(() => { setLoading(false); });
  }, [keyword, gateNeeded]);

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        key="psm-scrim"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2, ease: EASE }}
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          background: 'rgba(31,36,33,0.65)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          padding: '0',
        }}
        className="sm:items-center sm:p-5"
        onClick={onClose}
      >
        {gateNeeded ? (
          <AffiliateDisclosureGate
            disclosureText={disclosureText}
            onAccept={consent.accept}
            onDecline={onClose}
          />
        ) : (
        <motion.div
          key="psm-panel"
          initial={{ y: 48, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 32, opacity: 0 }}
          transition={spring}
          style={{
            width: '100%',
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--c-cream)',
            borderRadius: 'var(--r-xl) var(--r-xl) 0 0',
            boxShadow: 'var(--sh-deep)',
            overflow: 'hidden',
          }}
          className="sm:max-w-2xl sm:rounded-[var(--r-xl)]"
          onClick={(e) => { e.stopPropagation(); }}
        >
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            gap: 'var(--s-4)',
            padding: 'var(--s-5) var(--s-5) var(--s-4)',
            borderBottom: '1px solid var(--rule)',
            flexShrink: 0,
          }}>
            <div className="stack-2">
              <span className="t-label" style={{ color: 'var(--c-kivo-teal)' }}>Curated for you</span>
              <h3 className="t-h3" style={{ margin: 0 }}>
                {titleOverride ?? keyword}
              </h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                flexShrink: 0,
                width: 36, height: 36,
                borderRadius: '50%',
                border: '1px solid var(--rule)',
                background: 'var(--c-cream-weak)',
                display: 'grid', placeItems: 'center',
                cursor: 'pointer',
                color: 'var(--ink-muted)',
                transition: 'background var(--t-fast) var(--ease)',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-sand)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'var(--c-cream-weak)')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>

          {/* Content */}
          <div style={{
            flex: 1, overflowY: 'auto',
            padding: 'var(--s-4) var(--s-5)',
            background: 'var(--c-cream-weak)',
          }}>
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'var(--s-9) 0', gap: 'var(--s-4)' }}>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
                  style={{
                    width: 36, height: 36, borderRadius: '50%',
                    border: '2.5px solid var(--c-charcoal-12)',
                    borderTopColor: 'var(--c-kivo-teal)',
                  }}
                />
                <span className="t-label" style={{ color: 'var(--ink-muted)' }}>Searching…</span>
              </div>
            ) : results.length > 0 ? (
              <div className="stack-3">
                {results.map((product, idx) => (
                  <motion.a
                    key={idx}
                    href={product.product_link}
                    target="_blank"
                    rel="noreferrer"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.04, ...spring }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 'var(--s-4)',
                      padding: 'var(--s-4)',
                      background: 'var(--c-cream)',
                      borderRadius: 'var(--r-md)',
                      border: '1px solid var(--rule)',
                      boxShadow: 'var(--sh-soft)',
                      textDecoration: 'none',
                      color: 'inherit',
                      transition: 'box-shadow var(--t-fast) var(--ease), border-color var(--t-fast) var(--ease)',
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.borderColor = 'var(--c-kivo-teal)';
                      (e.currentTarget as HTMLElement).style.boxShadow = 'var(--sh-lifted)';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.borderColor = 'var(--rule)';
                      (e.currentTarget as HTMLElement).style.boxShadow = 'var(--sh-soft)';
                    }}
                  >
                    {/* Thumbnail */}
                    <div style={{
                      width: 68, height: 68, flexShrink: 0,
                      borderRadius: 'var(--r-sm)',
                      background: 'var(--c-sand)',
                      border: '1px solid var(--rule)',
                      display: 'grid', placeItems: 'center',
                      overflow: 'hidden',
                    }}>
                      {product.product_img_url ? (
                        <img
                          src={product.product_img_url}
                          alt={product.product_name}
                          style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }}
                        />
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--ink-muted)', opacity: 0.4 }}>
                          <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/>
                        </svg>
                      )}
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--s-1)' }}>
                      <div className="t-small" style={{ fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {product.product_name}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
                        {product.product_price && (
                          <span className="pill pill--teal" style={{ padding: '3px 8px', fontSize: 11 }}>
                            {typeof product.product_price === 'number' ? `$${product.product_price}` : product.product_price}
                          </span>
                        )}
                        <span className="t-label" style={{ color: 'var(--c-kivo-teal)' }}>View product</span>
                      </div>
                      {product.product_description && (
                        <p className="t-small t-mute" style={{ margin: 0, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}>
                          {product.product_description}
                        </p>
                      )}
                    </div>

                    {/* Arrow */}
                    <div style={{
                      flexShrink: 0, width: 32, height: 32,
                      borderRadius: '50%',
                      border: '1px solid var(--rule)',
                      display: 'grid', placeItems: 'center',
                      color: 'var(--ink-muted)',
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </div>
                  </motion.a>
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'var(--s-9) 0', gap: 'var(--s-3)', textAlign: 'center' }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: 'var(--c-sand)',
                  display: 'grid', placeItems: 'center',
                  color: 'var(--ink-muted)',
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                </div>
                <div className="stack-2">
                  <p className="t-small" style={{ margin: 0, fontWeight: 600, color: 'var(--ink)' }}>Nothing found</p>
                  <p className="t-small t-mute" style={{ margin: 0, maxWidth: '28ch' }}>
                    No recommended products matched this search.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: 'var(--s-4) var(--s-5)',
            borderTop: '1px solid var(--rule)',
            background: 'var(--c-cream)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--s-2)',
            flexShrink: 0,
          }}>
            {showDisclosure && (
              <p className="t-small t-mute" style={{ margin: 0, textAlign: 'center', maxWidth: '40ch' }}>
                {disclosureText}
              </p>
            )}
            <button
              type="button"
              onClick={onClose}
              className="btn btn--ghost btn--sm"
            >
              Back to analysis
            </button>
          </div>
        </motion.div>
        )}
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};
